import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ChevronDown, X, FileText, Sparkles, Eye, ScanLine, Save, Loader2 } from "lucide-react";
import { useState } from "react";

export interface TicketLineItem {
  sequence: number;
  item_code?: string;
  description: string;
  quantity: number;
  unit_price: number;
  uom?: string;
  discount?: number;
  line_total: number;
  price_book_item_id?: string;
}

export interface TicketExtractionData {
  vendor_name: string;
  ticket_number: string;
  ticket_date: string;
  subtotal?: number;
  tax_amount?: number;
  total_amount: number;
  currency?: string;
  notes?: string;
  line_items: TicketLineItem[];
}

export interface TicketExtractionResult {
  success: boolean;
  extraction_method: 'native_text' | 'ocr' | 'vision';
  ai_provider: string;
  page_count: number;
  confidence: number;
  data: TicketExtractionData;
  debug?: {
    processing_time_ms: number;
  };
  error?: string;
}

interface Props {
  results: TicketExtractionResult;
  onClear: () => void;
  onSave?: () => void;
  isSaving?: boolean;
}

export function TicketExtractionResults({ results, onClear, onSave, isSaving }: Props) {
  const [showRawJson, setShowRawJson] = useState(false);

  const getMethodBadge = (method: string) => {
    switch (method) {
      case 'native_text':
        return (
          <Badge variant="default" className="bg-green-600 hover:bg-green-700">
            <FileText className="w-3 h-3 mr-1" />
            Native Text
          </Badge>
        );
      case 'ocr':
        return (
          <Badge variant="default" className="bg-yellow-600 hover:bg-yellow-700">
            <ScanLine className="w-3 h-3 mr-1" />
            OCR
          </Badge>
        );
      case 'vision':
        return (
          <Badge variant="default" className="bg-orange-600 hover:bg-orange-700">
            <Eye className="w-3 h-3 mr-1" />
            Vision AI
          </Badge>
        );
      default:
        return <Badge variant="secondary">{method}</Badge>;
    }
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 85) return 'bg-green-500';
    if (confidence >= 70) return 'bg-yellow-500';
    return 'bg-orange-500';
  };

  const formatCurrency = (amount: number | undefined, currency: string = 'USD') => {
    if (amount === undefined || amount === null) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  const { data } = results;
  const currency = data.currency || 'USD';

  return (
    <div className="space-y-4">
      {/* Header with extraction info */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3 flex-wrap">
          {getMethodBadge(results.extraction_method)}
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Confidence:</span>
            <div className="flex items-center gap-2">
              <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
                <div 
                  className={`h-full ${getConfidenceColor(results.confidence)} transition-all`}
                  style={{ width: `${results.confidence}%` }}
                />
              </div>
              <span className="text-sm font-medium">{results.confidence}%</span>
            </div>
          </div>
          <Badge variant="outline">
            {results.page_count} page{results.page_count !== 1 ? 's' : ''}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={onClear}>
            <X className="w-4 h-4 mr-1" />
            Clear
          </Button>
          {onSave && (
            <Button size="sm" onClick={onSave} disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-1" />
                  Save Ticket
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Ticket Header Data */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary" />
            Extracted Ticket Data
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Vendor</p>
              <p className="font-medium text-sm">{data.vendor_name || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Ticket #</p>
              <p className="font-medium text-sm">{data.ticket_number || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Ticket Date</p>
              <p className="font-medium text-sm">{formatDate(data.ticket_date)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Currency</p>
              <p className="font-medium text-sm">{currency}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Total</p>
              <p className="font-medium text-primary">
                {formatCurrency(data.total_amount, currency)}
              </p>
            </div>
          </div>

          {data.notes && (
            <div className="mt-4 pt-4 border-t">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Notes</p>
              <p className="text-sm mt-1">{data.notes}</p>
            </div>
          )}

          {/* Totals Row */}
          <div className="mt-4 pt-4 border-t flex justify-end gap-6">
            <div className="text-right">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Subtotal</p>
              <p className="font-medium text-sm">{formatCurrency(data.subtotal, currency)}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Tax</p>
              <p className="font-medium text-sm">{formatCurrency(data.tax_amount, currency)}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Total</p>
              <p className="font-semibold">{formatCurrency(data.total_amount, currency)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Line Items */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">
            Line Items ({data.line_items?.length || 0})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">#</TableHead>
                  <TableHead className="w-24">Code</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right w-16">Qty</TableHead>
                  <TableHead className="text-right w-24">Unit Price</TableHead>
                  <TableHead className="w-16">UOM</TableHead>
                  <TableHead className="text-right w-16">Disc%</TableHead>
                  <TableHead className="text-right w-24">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.line_items?.length > 0 ? (
                  data.line_items.map((item, index) => (
                    <TableRow 
                      key={index}
                      className={item.price_book_item_id ? "bg-green-50 dark:bg-green-950/20 border-l-4 border-l-green-500" : ""}
                    >
                      <TableCell className="font-mono text-muted-foreground text-sm">
                        {item.sequence}
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        <div className="flex items-center gap-1">
                          {item.item_code || '-'}
                          {item.price_book_item_id && (
                            <Badge variant="outline" className="text-[10px] px-1 py-0 bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 border-green-300">
                              PB
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{item.description}</TableCell>
                      <TableCell className="text-right text-sm">{item.quantity}</TableCell>
                      <TableCell className="text-right text-sm">
                        {formatCurrency(item.unit_price, currency)}
                      </TableCell>
                      <TableCell className="text-sm">{item.uom || '-'}</TableCell>
                      <TableCell className="text-right text-sm">
                        {item.discount !== undefined && item.discount !== null ? `${item.discount}%` : '-'}
                      </TableCell>
                      <TableCell className="text-right font-medium text-sm">
                        {formatCurrency(item.line_total, currency)}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-muted-foreground py-6">
                      No line items extracted
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Raw JSON */}
      <Collapsible open={showRawJson} onOpenChange={setShowRawJson}>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" size="sm" className="w-full justify-between">
            <span className="text-muted-foreground text-sm">Raw JSON Response</span>
            <ChevronDown className={`w-4 h-4 transition-transform ${showRawJson ? 'rotate-180' : ''}`} />
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <Card className="mt-2">
            <CardContent className="p-4">
              <pre className="text-xs overflow-auto max-h-64 bg-muted p-3 rounded-lg">
                {JSON.stringify(results, null, 2)}
              </pre>
            </CardContent>
          </Card>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
